import { useState, useCallback, useMemo } from "react";
import { BlockStack, Button, Text, TextField, InlineStack, Box, Banner, RadioButton } from "@shopify/polaris";
import CustomModal from "../CustomModal/CustomModal";
import styles from "./ShowOnStoreModal.module.css";
import { endPoints, postData } from "../../utilities/networkCalls";
import { bundleTypeNameOptions } from "../../apps/constants";

type IssueType = "discount" | "display" | "design" | "setup" | null;

const COPY = {
    discount: {
        cta: "Send issue",
        hint: "Our team will investigate your discount setup",
        label: "Discount not applying correctly",
        desc: "The bundle price or discount isn't showing right at checkout",
        ctx: {
            online: {
                title: "We're on it",
                body: 'Click "Send issue" and our team will investigate your discount configuration right away and get back to you with a fix.',
            },
            offline: {
                title: "We'll pick this up first thing",
                body: "Our support team is currently offline. Click \"Send issue\" and we'll investigate your discount configuration when we're back and get back to you.",
            },
        },
        confirm: {
            online: {
                title: "Got it — we're on it",
                body: "Your discount issue has been sent to our team. We've also received your bundle details and store link.",
                eta: "Expected response: within 1–2 hours during business hours",
            },
            offline: {
                title: "Issue received — we'll pick it up first thing",
                body: "Our team is currently offline but your issue is in the queue. We'll investigate your discount configuration and get back to you when we're back online.",
                eta: "Expected response: next business day",
            },
        },
    },
    display: {
        cta: "Send issue",
        hint: "Our team will check your widget setup",
        label: "Bundle not displaying",
        desc: "The bundle widget isn't visible on my product page",
        ctx: {
            online: {
                title: "Let's get your bundle visible",
                body: 'Click "Send issue" and our team will check your theme integration and widget placement right away.',
            },
            offline: {
                title: "We'll look into this first thing",
                body: "Our support team is offline right now. Click \"Send issue\" and we'll check your bundle display when we're back.",
            },
        },
        confirm: {
            online: {
                title: "On it — checking your setup now",
                body: "Your bundle display issue has been flagged to our team. We've got your bundle name and store link and will get it sorted.",
                eta: "Expected response: within 1–2 hours during business hours",
            },
            offline: {
                title: "Issue received",
                body: "We're offline right now but your display issue is logged. Our team will check your theme integration and widget placement first thing tomorrow.",
                eta: "Expected response: next business day",
            },
        },
    },
    design: {
        cta: "Send request",
        hint: "Describe your design and we'll review feasibility",
        label: "Need custom design",
        desc: "I'd like the bundle to look different from the default",
        ctx: {
            online: {
                title: "Tell us what you have in mind",
                body: "Describe the custom design you're looking for below. Our team will review it and let you know what's possible and the estimated effort.",
            },
            offline: {
                title: "Tell us what you have in mind",
                body: "Describe the design you'd like below. Our team will review it and respond with what's possible and an effort estimate.",
            },
        },
        confirm: {
            online: {
                title: "Design request received",
                body: "Thanks for describing what you're looking for. Our team will review your request and get back to you with what's possible and a rough effort estimate.",
                eta: "Expected response: within 1 business day",
            },
            offline: {
                title: "Design request logged",
                body: "We're offline right now but your custom design request is in. Our team will review it and come back to you with what's feasible and an effort estimate.",
                eta: "Expected response: next business day",
            },
        },
    },
    setup: {
        cta: "Contact support",
        hint: "We'll help you configure it correctly",
        label: "Need help setting this up",
        desc: "Not sure if the bundle is configured correctly",
        ctx: {
            online: {
                title: "We'll walk you through it",
                body: 'Click "Contact support" and our team will help you configure the bundle exactly the way you want it — step by step.',
            },
            offline: {
                title: "We'll walk you through it",
                body: "Our team is currently offline. Click \"Contact support\" and we'll reach out to walk you through the setup when we're back.",
            },
        },
        confirm: {
            online: {
                title: "We'll be in touch shortly",
                body: "Our team has your setup request and will reach out to walk you through the configuration step by step.",
                eta: "Expected response: within a few hours during business hours",
            },
            offline: {
                title: "Request received",
                body: "Our team is offline but your setup request is logged. We'll reach out first thing to walk you through the configuration.",
                eta: "Expected response: next business day",
            },
        },
    },
};

const isSupportOnline = () => {
    const now = new Date();

    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const istMinutesTotal = utcHours * 60 + utcMinutes + (5 * 60 + 30);

    let istHour = Math.floor(istMinutesTotal / 60) % 24;
    if (istHour < 0) istHour += 24;

    if ((istHour >= 10 && istHour < 24) || (istHour >= 0 && istHour < 2)) {
        return true;
    }
    return false;
};

interface ShowOnStoreModalProps {
    open: boolean;
    onClose: () => void;
    bundleType: string | number;
    bundleLink?: string;
}

const ShowOnStoreModal = ({ open, onClose, bundleType, bundleLink }: ShowOnStoreModalProps) => {
    const [selectedIssue, setSelectedIssue] = useState<IssueType>(null);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);

    const isOnline = useMemo(() => isSupportOnline(), [open]);

    const handleClose = useCallback(() => {
        onClose();
        setTimeout(() => {
            setSelectedIssue(null);
            setMessage("");
            setIsConfirmed(false);
        }, 300);
    }, [onClose]);

    const handleSubmitIssue = async () => {
        if (!selectedIssue) return;

        if (selectedIssue === "setup") {
            if (window.kefi_chat?.open) {
                window.kefi_chat.open();
            } else {
                console.warn("kefi_chat not available");
            }
            handleClose();
            return;
        }

        setLoading(true);

        const res = await postData({
            url: endPoints.sendShowOnStoreFeedback,
            payload: {
                bundleType,
                issueType: COPY[selectedIssue].label,
                message,
                bundleLink,
            },
        });

        setLoading(false);
        if (res.status === 200) {
            setIsConfirmed(true);
        } else {
            shopify.toast.show("Failed to send feedback. Please try again.", { isError: true });
        }
    };

    const renderContent = () => {
        if (isConfirmed) {
            const mode = isOnline ? "online" : "offline";
            const conf = COPY[selectedIssue!].confirm[mode];

            return (
                <Box padding="600">
                    <BlockStack align="center" inlineAlign="center" gap="400">
                        <div className={`${styles.confirmIcon} ${isOnline ? styles.iconOnline : styles.iconOffline}`}>
                            {isOnline ? (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path
                                        d="M4 10.5l4 4 8-8"
                                        stroke="#27500A"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 5v5.5" stroke="#633806" strokeWidth="2" strokeLinecap="round" />
                                    <circle cx="10" cy="14.5" r="1.3" fill="#633806" />
                                </svg>
                            )}
                        </div>
                        <BlockStack align="center" inlineAlign="center" gap="100">
                            <Text variant="headingMd" as="h3">
                                {conf.title}
                            </Text>
                            <Box maxWidth="380px">
                                <Text variant="bodyMd" as="p" tone="subdued" alignment="center">
                                    {conf.body}
                                </Text>
                            </Box>
                        </BlockStack>
                        <Box
                            background="bg-surface-secondary"
                            padding="150"
                            paddingInlineStart="300"
                            paddingInlineEnd="300"
                            borderRadius="300"
                        >
                            <Text variant="bodySm" tone="subdued" as="span">
                                {conf.eta}
                            </Text>
                        </Box>
                        <Box paddingBlockStart="200">
                            <Button onClick={handleClose}>Done</Button>
                        </Box>
                    </BlockStack>
                </Box>
            );
        }

        const choicesList = (["discount", "display", "design", "setup"] as IssueType[]).map((id) => {
            const isSelected = selectedIssue === id;
            const mode = isOnline ? "online" : "offline";
            const ctx = COPY[id!].ctx[mode];

            return (
                <div key={id!} onClick={() => setSelectedIssue(id!)} style={{ cursor: "pointer" }}>
                    <Box
                        padding="300"
                        borderWidth="025"
                        borderColor={isSelected ? "border-inverse" : "border"}
                        borderRadius="200"
                        background={isSelected ? "bg-surface-secondary" : "bg-surface"}
                    >
                        <InlineStack gap="200">
                            <RadioButton label="" checked={isSelected} onChange={() => setSelectedIssue(id!)} />
                            <BlockStack gap="050">
                                <Text as="p">{COPY[id!].label}</Text>
                                <Text as="p" tone="subdued">
                                    {COPY[id!].desc}
                                </Text>
                            </BlockStack>
                        </InlineStack>
                    </Box>
                </div>
            );
        });

        return (
            <Box padding="400">
                <BlockStack gap="400">
                    <Text variant="headingXs" as="h3" tone="subdued">
                        HOW IS THE BUNDLE WORKING FOR YOU?
                    </Text>
                    <BlockStack gap="200">{choicesList}</BlockStack>
                    {selectedIssue && (
                        <Box paddingBlockStart="100">
                            <Banner
                                tone={isOnline ? "info" : "warning"}
                                title={COPY[selectedIssue].ctx[isOnline ? "online" : "offline"].title}
                            >
                                <BlockStack gap="300">
                                    <Text as="p">{COPY[selectedIssue].ctx[isOnline ? "online" : "offline"].body}</Text>
                                    {selectedIssue === "design" && (
                                        <TextField
                                            label="Design details"
                                            labelHidden
                                            placeholder="e.g. Use my brand colors (navy + gold), hide product prices, show a progress bar instead of checkboxes..."
                                            value={message}
                                            onChange={(val) => setMessage(val)}
                                            multiline={3}
                                            autoComplete="off"
                                        />
                                    )}
                                </BlockStack>
                            </Banner>
                        </Box>
                    )}
                </BlockStack>
            </Box>
        );
    };

    const titleText = isConfirmed
        ? isOnline
            ? "Message sent"
            : "Message received"
        : `You've published your first ${bundleTypeNameOptions.find((opt) => opt.id === bundleType)?.label} bundle!`;

    const subText = isConfirmed
        ? isOnline
            ? "Our team has been notified"
            : "We'll be in touch when we're back online"
        : "Is everything working as expected?";

    const titleMarkup = (
        <BlockStack gap="050">
            <Text variant="headingMd" as="h2">
                {titleText}
            </Text>
            <Text variant="bodySm" tone="subdued" as="p">
                {subText}
            </Text>
        </BlockStack>
    );

    return (
        <CustomModal
            open={open}
            onClose={handleClose}
            title={titleMarkup}
            footer={
                isConfirmed ? undefined : (
                    <Text tone="subdued" variant="bodySm" as="span">
                        {selectedIssue ? COPY[selectedIssue].hint : "Select an option above"}
                    </Text>
                )
            }
            primaryAction={
                isConfirmed
                    ? undefined
                    : {
                          content: selectedIssue ? COPY[selectedIssue].cta : "Contact support",
                          disabled: !selectedIssue || loading,
                          loading: loading,
                          onAction: handleSubmitIssue,
                      }
            }
            secondaryActions={
                isConfirmed
                    ? undefined
                    : [
                          {
                              content: "Everything looks good",
                              onAction: handleClose,
                          },
                      ]
            }
        >
            {renderContent()}
        </CustomModal>
    );
};

export default ShowOnStoreModal;
